import { PriorityLevel, UserDto } from '@act/shared/data-transfer-objects';
import { Card, CardContent, IconButton, Typography } from '@mui/material';
import { displayName } from 'react-components/docket/components/utilities/displayNameAndBirthdayCheck.utils';
import { IconLibrary } from '../../../display/IconLibrary/IconLibrary';
import { MemberStatusTag } from '../../../display/TaggableChip/MemberStatusTag';
import { FlexContainer } from '../../ContentContainers/FlexContainer';
import { ContactSoonPhaseAndPriority } from './ContactSoonPhaseAndPriority';
import { DocketMobileCardSubHeader } from './DocketMobileCardSubHeader';

interface DocketMobileCardProps {
  date?: string;
  member: UserDto;
  outreachPhase?: string;
  priorityLevel?: PriorityLevel;
  topic?: string;
  time?: string;
  gotoAction?: () => void;
  openMenu?: (event: React.MouseEvent<HTMLElement>, data: UserDto) => void;
}

export const DocketMobileCard = ({
  date,
  member,
  outreachPhase,
  priorityLevel,
  topic,
  time,
  gotoAction,
  openMenu,
}: DocketMobileCardProps) => {
  function formatTopic(topic?: string): string {
    if (!topic || typeof topic !== 'string') {
      return topic || '';
    }

    return topic.replace(/\b([A-Z_]+)\b/g, match => {
      if (match.includes('_')) {
        const firstWord = match.split('_')[0];
        if (!firstWord) {
          return match;
        }
        return (
          firstWord.charAt(0).toUpperCase() + firstWord.slice(1).toLowerCase()
        );
      }
      return match;
    });
  }

  function getTruncatedDisplayName(member: UserDto, maxLength = 10): string {
    const fullName = displayName(member, { hideBirthday: true });

    if (!fullName || fullName === 'Unknown') {
      return fullName;
    }

    if (fullName.length <= maxLength) {
      return displayName(member);
    }

    if (member.firstName && member.lastName) {
      const truncatedName = `${member.firstName} ${member.lastName.charAt(0)}.`;
      const birthday = displayName(member).match(/🎂/)?.[0] || '';
      return `${truncatedName} ${birthday}`.trim();
    }

    return displayName(member);
  }

  return (
    <Card
      sx={{
        padding: '16px 16px 8px',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      <CardContent
        sx={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
        }}
      >
        <FlexContainer
          direction="row"
          gap={2}
          width="100%"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
          }}
        >
          <FlexContainer
            direction="row"
            gap={1.25}
            alignItems="center"
            flexShrink={1}
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              flex: 1,
            }}
          >
            <Typography
              variant="titleMedium"
              noWrap
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              {getTruncatedDisplayName(member)}
            </Typography>
            <MemberStatusTag status={member?.memberStatus} />
          </FlexContainer>
          <FlexContainer direction="row" gap={0} flexShrink={0}>
            <IconButton
              disabled={!openMenu}
              onClick={event => openMenu(event, member)}
            >
              <IconLibrary
                iconName="DotsThreeVertical"
                size={20}
                weight="regular"
              />
            </IconButton>
            <IconButton onClick={gotoAction}>
              <IconLibrary iconName="CaretRight" size={20} weight="regular" />
            </IconButton>
          </FlexContainer>
        </FlexContainer>
        <FlexContainer
          direction="column"
          gap={1}
          width="100%"
          sx={{
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
          }}
        >
          <DocketMobileCardSubHeader
            topic={formatTopic(topic)}
            date={date}
            time={time}
          />
          {outreachPhase && priorityLevel && (
            <ContactSoonPhaseAndPriority
              outreachPhase={outreachPhase}
              priorityLevel={priorityLevel}
            />
          )}
        </FlexContainer>
      </CardContent>
    </Card>
  );
};
